"use client";

import { cn } from "@ui/lib";
import {
	DownloadIcon,
	EditIcon,
	MoreHorizontalIcon,
	StarIcon,
	TrashIcon,
	VideoIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@ui/components/dropdown-menu";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@ui/components/tooltip";

export type ImageActionLabels = {
	generateVideo: string;
	download: string;
	more: string;
	edit: string;
	delete: string;
	favorite: string;
};

/** 工具条上的按钮都长一个样，只有图标和动作不同；tooltip 直接用 aria-label 那句 */
function ToolbarButton({
	label,
	onClick,
	className,
	children,
}: {
	label: string;
	onClick: () => void;
	className?: string;
	children: ReactNode;
}) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					aria-label={label}
					onClick={onClick}
					className={cn(
						"rounded-full p-1 transition-colors hover:bg-white/20 sm:p-1.5",
						className,
					)}
				>
					{children}
				</button>
			</TooltipTrigger>
			<TooltipContent side="bottom">{label}</TooltipContent>
		</Tooltip>
	);
}

/** Hover toolbar shown on a history image: generate video, download, a "…" menu
 *  holding edit/delete, and the favorite toggle. */
export function HistoryImageActions({
	labels,
	onGenerateVideo,
	onDownload,
	onEdit,
	onDelete,
	onFavorite,
	isFavorite,
}: {
	labels: ImageActionLabels;
	onGenerateVideo?: () => void;
	onDownload: () => void;
	onEdit?: () => void;
	onDelete?: () => void;
	onFavorite?: () => void;
	isFavorite?: boolean;
}) {
	const [menuOpen, setMenuOpen] = useState(false);

	return (
		// 工具条本来就要 hover 才出来，tooltip 再等 700ms 就太迟了
		<TooltipProvider delayDuration={200}>
			{/* 藏起来等 hover 只对真有指针的设备成立。按屏幕宽度判断的话，
			    平板和横屏手机宽度够了却没有 hover，工具条就永远点不到 */}
			<div
				className={cn(
					"pointer-events-none absolute top-2 right-2 transition-opacity opacity-100 [@media(hover:hover)]:opacity-0 group-hover:opacity-100",
					menuOpen && "[@media(hover:hover)]:opacity-100",
				)}
			>
				{/* 两列网格时一块图只有 ~160px 宽，工具条按桌面尺寸会顶出图外，
				    窄屏整体收一档 */}
				<div className="pointer-events-auto flex items-center gap-0.5 rounded-full bg-black/60 px-1 py-1 text-white backdrop-blur-sm touch-pan-y sm:px-1.5">
					{onGenerateVideo && (
						<ToolbarButton
							label={labels.generateVideo}
							onClick={onGenerateVideo}
						>
							<VideoIcon className="h-4 w-4" />
						</ToolbarButton>
					)}
					<ToolbarButton label={labels.download} onClick={onDownload}>
						<DownloadIcon className="h-4 w-4" />
					</ToolbarButton>
					{/* 编辑要有图片表单才有意义，视频页只给删除；两个都没有就不出这个菜单 */}
					{(onEdit || onDelete) && (
						<DropdownMenu
							open={menuOpen}
							onOpenChange={setMenuOpen}
						>
							{/* 菜单开着的时候不要再弹 tooltip 挡住菜单 */}
							<Tooltip open={menuOpen ? false : undefined}>
								<TooltipTrigger asChild>
									<DropdownMenuTrigger asChild>
										<button
											type="button"
											aria-label={labels.more}
											className="rounded-full p-1 transition-colors hover:bg-white/20 sm:p-1.5"
										>
											<MoreHorizontalIcon className="h-4 w-4" />
										</button>
									</DropdownMenuTrigger>
								</TooltipTrigger>
								<TooltipContent side="bottom">
									{labels.more}
								</TooltipContent>
							</Tooltip>
							<DropdownMenuContent
								align="end"
								className="w-40 border-white/10 bg-black/60 text-white backdrop-blur-md"
							>
								{onEdit && (
									<DropdownMenuItem
										onClick={onEdit}
										className="focus:bg-white/20 focus:text-white"
									>
										<EditIcon className="mr-2 h-4 w-4" />
										{labels.edit}
									</DropdownMenuItem>
								)}
								{onDelete && (
									<DropdownMenuItem
										onClick={onDelete}
										className="text-red-500 focus:bg-white/20 focus:text-red-400"
									>
										<TrashIcon className="mr-2 h-4 w-4" />
										{labels.delete}
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
					{onFavorite && (
						<>
							<span className="mx-0.5 h-4 w-px bg-white/30" />
							<ToolbarButton
								label={labels.favorite}
								onClick={onFavorite}
								className={cn(isFavorite && "text-yellow-400")}
							>
								<StarIcon
									className={cn(
										"h-4 w-4",
										isFavorite && "fill-current",
									)}
								/>
							</ToolbarButton>
						</>
					)}
				</div>
			</div>
		</TooltipProvider>
	);
}
